import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingDown, TrendingUp, CheckCircle2, AlertCircle, Pencil, RefreshCw } from "lucide-react";
import { formatEur, formatPct } from "@/lib/index";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface SupplierReportProps {
  grossP1: number;
  grossP2: number;
  netP1: number;
  netP2: number;
  p1Contribution: number;
  p2Contribution: number;
  furnitureCost: number;
  includeFurniture: boolean;
  savingsPct: number;
  savingsEur: number;
  litros: number;
  eurPerLitro: number;
  billingBenefit: number;
  p1RappelAmount: number;
  p2RappelAmount: number;
}

function EditableCell({
  value,
  onChange,
  className = "",
  prefix = "",
}: {
  value: number;
  onChange: (v: number) => void;
  className?: string;
  prefix?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState(value.toString());

  useEffect(() => {
    if (!editing) setRaw(value.toFixed(2));
  }, [value, editing]);

  if (editing) {
    return (
      <Input
        autoFocus
        type="number"
        step="0.01"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          const parsed = parseFloat(raw);
          if (!isNaN(parsed)) onChange(parsed);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const parsed = parseFloat(raw);
            if (!isNaN(parsed)) onChange(parsed);
            setEditing(false);
          }
          if (e.key === "Escape") setEditing(false);
        }}
        className={`w-32 text-right font-mono text-sm h-8 ${className}`}
      />
    );
  }

  return (
    <button
      onClick={() => { setEditing(true); setRaw(value.toFixed(2)); }}
      className={`group flex items-center gap-1 justify-end w-full font-mono text-sm hover:opacity-80 transition-opacity ${className}`}
      title="Haz clic para editar"
    >
      <span>{prefix}{formatEur(value)}</span>
      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity shrink-0" />
    </button>
  );
}

export function SupplierReport({
  grossP1,
  grossP2,
  p1Contribution,
  p2Contribution,
  furnitureCost,
  includeFurniture,
  litros,
  eurPerLitro,
  billingBenefit,
  p1RappelAmount,
  p2RappelAmount,
}: SupplierReportProps) {
  const [vals, setVals] = useState({
    grossP1,
    grossP2,
    billingBenefit,
    p1Contribution,
    p2Contribution,
    furnitureCost,
    p1RappelAmount,
    p2RappelAmount,
  });

  useEffect(() => {
    setVals({
      grossP1,
      grossP2,
      billingBenefit,
      p1Contribution,
      p2Contribution,
      furnitureCost,
      p1RappelAmount,
      p2RappelAmount,
    });
  }, [grossP1, grossP2, billingBenefit, p1Contribution, p2Contribution, furnitureCost, p1RappelAmount, p2RappelAmount]);

  const set = (field: keyof typeof vals) => (v: number) =>
    setVals((prev) => ({ ...prev, [field]: v }));

  const resetAll = () =>
    setVals({ grossP1, grossP2, billingBenefit, p1Contribution, p2Contribution, furnitureCost, p1RappelAmount, p2RappelAmount });

  const calcNetP1 =
    vals.grossP1 - vals.p1Contribution - vals.billingBenefit - vals.p1RappelAmount;
  const calcNetP2 =
    vals.grossP2 - vals.p2Contribution + (includeFurniture ? vals.furnitureCost : 0) + vals.billingBenefit - vals.p2RappelAmount;

  const calcSavingsEur = calcNetP2 - calcNetP1;
  const calcSavingsPct = calcNetP2 !== 0 ? (calcSavingsEur / calcNetP2) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.6 }}
      className="space-y-8"
    >
      <Card className="border-border/50 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold">Informe Comparativo de Proveedores</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetAll}
              className="gap-2 text-muted-foreground hover:text-foreground"
              title="Restaurar valores calculados"
            >
              <RefreshCw className="w-4 h-4" />
              Restaurar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Pencil className="w-3 h-3" />
            Haz clic sobre cualquier valor para editarlo manualmente
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Concepto</th>
                  <th className="text-right py-3 px-4 font-semibold text-primary">Proveedor 1</th>
                  <th className="text-right py-3 px-4 font-semibold text-accent">Proveedor 2</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 font-medium">Coste Bruto (c/dto factura)</td>
                  <td className="py-2 px-4">
                    <EditableCell value={vals.grossP1} onChange={set("grossP1")} />
                  </td>
                  <td className="py-2 px-4">
                    <EditableCell value={vals.grossP2} onChange={set("grossP2")} />
                  </td>
                </tr>

                <tr className="hover:bg-muted/50 transition-colors bg-success/10">
                  <td className="py-3 px-4 font-medium">Beneficio Facturación P1</td>
                  <td className="py-2 px-4">
                    <EditableCell
                      value={vals.billingBenefit}
                      onChange={set("billingBenefit")}
                      className="text-success font-semibold"
                      prefix="-"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <span className="flex justify-end font-mono text-sm text-destructive font-semibold">
                      +{formatEur(vals.billingBenefit)}
                    </span>
                  </td>
                </tr>

                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 font-medium">Aportación Directa</td>
                  <td className="py-2 px-4">
                    <EditableCell
                      value={vals.p1Contribution}
                      onChange={set("p1Contribution")}
                      className="text-destructive"
                      prefix="-"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <EditableCell
                      value={vals.p2Contribution}
                      onChange={set("p2Contribution")}
                      className="text-destructive"
                      prefix="-"
                    />
                  </td>
                </tr>

                {includeFurniture && (
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4 font-medium">Mobiliario</td>
                    <td className="py-3 px-4 text-right font-mono text-sm text-muted-foreground">n/a</td>
                    <td className="py-2 px-4">
                      <EditableCell
                        value={vals.furnitureCost}
                        onChange={set("furnitureCost")}
                        className="text-destructive"
                        prefix="+"
                      />
                    </td>
                  </tr>
                )}

                <tr className="hover:bg-muted/50 transition-colors bg-success/10">
                  <td className="py-3 px-4 font-medium">
                    <div className="flex flex-col">
                      <span>Rappel fin de temporada</span>
                      <span className="text-xs text-muted-foreground font-normal">Ingreso recibido al cierre</span>
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <EditableCell
                      value={vals.p1RappelAmount}
                      onChange={set("p1RappelAmount")}
                      className="text-success font-semibold"
                      prefix="-"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <EditableCell
                      value={vals.p2RappelAmount}
                      onChange={set("p2RappelAmount")}
                      className="text-success font-semibold"
                      prefix="-"
                    />
                  </td>
                </tr>

                <tr className="bg-muted/30 font-bold border-t-2 border-border">
                  <td className="py-4 px-4 text-lg">COSTE NETO</td>
                  <td className="text-right py-4 px-4 font-mono text-lg text-primary">
                    {formatEur(calcNetP1)}
                  </td>
                  <td className="text-right py-4 px-4 font-mono text-lg text-accent">
                    {formatEur(calcNetP2)}
                  </td>
                </tr>

                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 font-medium">Diferencia</td>
                  <td className="text-right py-3 px-4 font-mono" colSpan={2}>
                    <Badge variant="outline" className="text-base px-3 py-1">
                      {formatEur(calcSavingsEur)}
                    </Badge>
                  </td>
                </tr>

                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 font-medium">Ahorro %</td>
                  <td className="text-right py-3 px-4 font-mono" colSpan={2}>
                    <Badge className="bg-success hover:bg-success/90 text-success-foreground text-base px-3 py-1">
                      {formatPct(calcSavingsPct)}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
              <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Detalle Aportación P2:</h4>
              <p className="font-mono text-sm">
                {litros.toLocaleString('es-ES')} litros × {formatEur(eurPerLitro)}/litro = {formatEur(p2Contribution)}
              </p>
            </div>
            <div className="bg-success/10 rounded-lg p-4 border border-success/20">
              <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Rappel total recibido:</h4>
              <p className="font-mono text-sm">
                P1: <span className="text-success font-semibold">{formatEur(vals.p1RappelAmount)}</span>
                &nbsp;·&nbsp;
                P2: <span className="text-success font-semibold">{formatEur(vals.p2RappelAmount)}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-primary/30 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="w-5 h-5" />
              Proveedor 1
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-sm text-success mb-2 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                Ventajas
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Coste neto anual {formatEur(calcSavingsEur)} más bajo</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Beneficio adicional de {formatEur(vals.billingBenefit)} por precios de facturación más bajos</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Rappel fin de temporada: {formatEur(vals.p1RappelAmount)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Aportación fija predecible de {formatEur(vals.p1Contribution)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>No requiere inversión adicional en mobiliario</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Ahorro del {formatPct(calcSavingsPct)} respecto a P2</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-destructive mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Desventajas
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">•</span>
                  <span>Aportación anual fija independiente del volumen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">•</span>
                  <span>Menor rappel fin de temporada que P2 ({formatEur(vals.p2RappelAmount - vals.p1RappelAmount)} menos)</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="border-accent/30 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-accent">
              <AlertCircle className="w-5 h-5" />
              Proveedor 2
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-sm text-success mb-2 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                Ventajas
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Mayor rappel fin de temporada: {formatEur(vals.p2RappelAmount)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Aportación variable según volumen de ventas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Mayor aportación total ({formatEur(vals.p2Contribution)})</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span>Potencial de mayor descuento con aumento de volumen</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-destructive mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Desventajas
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">•</span>
                  <span>Coste neto anual {formatEur(calcSavingsEur)} más alto</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">•</span>
                  <span>Penalización de {formatEur(vals.billingBenefit)} por precios de facturación más altos</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">•</span>
                  <span>Precios unitarios superiores en productos clave</span>
                </li>
                {includeFurniture && (
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-0.5">•</span>
                    <span>Requiere inversión adicional de {formatEur(vals.furnitureCost)} en mobiliario</span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">•</span>
                  <span>Aportación variable menos predecible</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-primary/50 bg-gradient-to-br from-primary/5 to-primary/10 shadow-xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-primary" />
            Conclusión y Recomendación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-background/80 backdrop-blur rounded-lg p-6 border border-primary/20">
            <p className="text-lg font-semibold text-primary mb-3">
              Se recomienda firmemente la contratación con Proveedor 1
            </p>
            <div className="space-y-2 text-sm">
              <p className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">1.</span>
                <span>
                  <strong>Ahorro significativo:</strong> Representa un ahorro neto de {formatEur(calcSavingsEur)} anuales ({formatPct(calcSavingsPct)}), lo que supone una reducción sustancial en costes operativos.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">2.</span>
                <span>
                  <strong>Ventaja en facturación:</strong> Los precios de facturación más bajos de P1 generan un beneficio adicional de {formatEur(vals.billingBenefit)}, que se suma directamente a la rentabilidad.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">3.</span>
                <span>
                  <strong>Rappel fin de temporada:</strong> P1 devuelve {formatEur(vals.p1RappelAmount)} al cierre. Aunque P2 ofrece {formatEur(vals.p2RappelAmount)}, el ahorro total con P1 sigue siendo superior.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">4.</span>
                <span>
                  <strong>Previsibilidad financiera:</strong> La aportación fija de {formatEur(vals.p1Contribution)} permite una mejor planificación presupuestaria sin sorpresas vinculadas a variaciones de volumen.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">5.</span>
                <span>
                  <strong>Sin inversiones adicionales:</strong> No requiere desembolso extra en mobiliario, evitando {formatEur(vals.furnitureCost)} de coste adicional que sí exigiría P2.
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
              Análisis 2026
            </Badge>
            <span>•</span>
            <span>Basado en datos actuales de aprovisionamiento</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}